export type AnalyticsSection =
  | 'users'
  | 'social'
  | 'projects'
  | 'quotations'
  | 'jobs'
  | 'marketplace'
  | 'broker'
  | 'catalog'
  | 'platform';

export type MetricTrend = 'up' | 'down' | 'flat' | 'na';

export interface MetricResult {
  key: string;
  label: string;
  section: AnalyticsSection;
  current: number;
  previous: number;
  delta: number;
  deltaPercent: number | null;
  trend: MetricTrend;
  dimensions: Record<string, string>;
}

export interface AnalyticsOverviewResponse {
  period: {
    from: string;
    to: string;
    compareFrom: string;
    compareTo: string;
    compareMode: 'auto' | 'explicit';
    timezone: string;
  };
  sections: Partial<Record<AnalyticsSection, { metrics: MetricResult[] }>>;
  report: {
    meta: {
      title: string;
      generatedAt: string;
      timezone: string;
      periodLabel: string;
      comparePeriodLabel: string;
      compareMode: 'auto' | 'explicit';
      filtersApplied: {
        includeDeleted: boolean;
        usersValidatedOnly: boolean;
        sections: AnalyticsSection[] | 'all';
      };
    };
    rows: Array<{
      section: AnalyticsSection;
      sectionLabel: string;
      metricKey: string;
      metricLabel: string;
      current: number;
      previous: number;
      delta: number;
      deltaPercent: number | null;
      trend: MetricTrend;
      dimensions: Record<string, string>;
    }>;
  };
}

export interface AnalyticsReportResponse {
  meta: AnalyticsOverviewResponse['report']['meta'];
  rows: AnalyticsOverviewResponse['report']['rows'];
}

export interface AdminAnalyticsFilters {
  from: string;
  to: string;
  compareFrom: string | null;
  compareTo: string | null;
  useExplicitCompare: boolean;
  includeDeleted: boolean;
  usersValidatedOnly: boolean;
  activeSection: AnalyticsSection | 'all';
}

export const ANALYTICS_SECTIONS: AnalyticsSection[] = [
  'users',
  'social',
  'projects',
  'quotations',
  'jobs',
  'marketplace',
  'broker',
  'catalog',
  'platform',
];

export const ANALYTICS_SECTION_LABELS: Record<AnalyticsSection, string> = {
  users: 'Usuarios',
  social: 'Social y muro',
  projects: 'Obras',
  quotations: 'Cotizaciones',
  jobs: 'Bolsa de trabajo',
  marketplace: 'Marketplace',
  broker: 'Seguros',
  catalog: 'Catálogo',
  platform: 'Plataforma',
};

export type UserActivityWindowDays = 30 | 60 | 90 | 180 | 360;

export type UserActivityScope = 'within' | 'band' | 'no_activity' | 'inactive_over';

export interface UserActivityQueryParams {
  windowDays: UserActivityWindowDays;
  scope?: UserActivityScope;
  asOf?: string;
  includeDeleted?: boolean;
  usersValidatedOnly?: boolean;
  page?: number;
  take?: number;
}

export interface UserActivityItem {
  id: number;
  name: string;
  last_name: string;
  username: string;
  last_login: string | null;
  validated: boolean;
  type: string;
  created_at: string;
  rols: string[];
}

export interface UserActivityResponse {
  meta: {
    windowDays: UserActivityWindowDays;
    scope: UserActivityScope;
    asOf: string;
    cutoffFrom: string;
    cutoffTo: string | null;
    filtersApplied: {
      includeDeleted: boolean;
      usersValidatedOnly: boolean;
    };
  };
  items: UserActivityItem[];
  page: number;
  take: number;
  itemCount: number;
}
